import React, { useCallback, useMemo, useState } from "react";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { CharacteristicField } from "components/modals/Deal-create/components/CharacteristicField";
import { Characteristic, CharacteristicDef } from "store/types";

const getTabIcon = (tab: string, isActive: boolean) => {
  const color = isActive ? "#071f35" : "#496682";
  const sx = { fontSize: 16, color };
  switch (tab) {
    case "General":
      return <InfoOutlinedIcon sx={sx} />;
    case "Legal":
      return <ArticleOutlinedIcon sx={sx} />;
    case "Financial":
      return <AssessmentOutlinedIcon sx={sx} />;
    default:
      return null;
  }
};

interface CharacteristicData {
  characteristic_id: number;
  value: string;
  name?: string;
  currency?: string | null;
}

interface OnboardingCharacteristicsSectionProps {
  characteristics: CharacteristicData[];
  characteristicsList: Characteristic[];
  categories: string[];
  onCharacteristicChange: (charId: number, value: string) => void;
  onCurrencyChange: (charId: number, currency: string | null) => void;
}

export const OnboardingCharacteristicsSection: React.FC<OnboardingCharacteristicsSectionProps> = ({
  characteristics,
  characteristicsList,
  categories,
  onCharacteristicChange,
  onCurrencyChange
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Create characteristic definitions from all available characteristics (like CharacteristicsScreen)
  const characteristicDefs = useMemo<CharacteristicDef[]>(
    () =>
      characteristicsList
        .filter((c) => c.category_name !== "Asset Report")
        .map((item) => ({
          id: item.id,
          name: item.name,
          category_name: item.category_name,
          data_type: item.data_type,
          is_multiple: item.is_multiple,
          list_options: item.list_options,
          required: false
        })),
    [characteristicsList]
  );

  // Tabs from API categories (exactly like CharacteristicsScreen)
  const tabs = useMemo(() => {
    return categories.filter((c) => c !== "Contact");
  }, [categories]);

  const currentTab = tabs[activeTab] || tabs[0];

  // Track which tabs have filled values
  const filledTabs = useMemo(() => {
    const filled = new Set<string>();
    characteristics.forEach((char) => {
      if (!char.value) return;
      const def = characteristicDefs.find((d) => d.id === char.characteristic_id);
      if (def?.category_name) {
        filled.add(def.category_name);
      }
    });
    return filled;
  }, [characteristics, characteristicDefs]);

  // Characteristics for current tab
  const currentCharacteristics = useMemo(() => {
    if (!currentTab) return [];
    return characteristicDefs.filter((c) => c.category_name === currentTab);
  }, [currentTab, characteristicDefs]);

  const getCharValue = useCallback(
    (charId: number): string => {
      return characteristics.find((c) => c.characteristic_id === charId)?.value || "";
    },
    [characteristics]
  );

  const getCharCurrency = useCallback(
    (charId: number): string | null => {
      return characteristics.find((c) => c.characteristic_id === charId)?.currency ?? null;
    },
    [characteristics]
  );

  if (characteristicDefs.length === 0 || tabs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f6f8fc",
        p: "20px",
        borderRadius: "8px"
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: "rgba(10, 49, 66, 0.7)",
          mb: 2,
          textTransform: "uppercase",
          letterSpacing: "0.5px"
        }}
      >
        Extracted Characteristics
      </Typography>

      {/* Tabs */}
      <Stack sx={{ borderBottom: "1px solid #dbe8f5", mb: 3 }}>
        <Stack direction="row" alignItems="stretch" sx={{ gap: "16px" }}>
          {tabs.map((tab, index) => (
            <React.Fragment key={tab}>
              {!!index && <Box sx={{ width: "1px", bgcolor: "#dbe8f5", my: "6px" }} />}
              <Stack
                direction="row"
                alignItems="center"
                onClick={() => setActiveTab(index)}
                sx={{
                  gap: "5px",
                  pb: "6px",
                  pt: "2px",
                  cursor: "pointer",
                  borderBottom: activeTab === index ? "2px solid #071f35" : "2px solid transparent",
                  mb: "-1px"
                }}
              >
                {getTabIcon(tab, activeTab === index)}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "16px",
                    color: activeTab === index ? "#071f35" : "#496682",
                    whiteSpace: "nowrap"
                  }}
                >
                  {tab}
                </Typography>
                {filledTabs.has(tab) && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#26a6ae",
                      flexShrink: 0,
                      mb: "10px"
                    }}
                  />
                )}
              </Stack>
            </React.Fragment>
          ))}
        </Stack>
      </Stack>

      {/* Tab Content */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap="20px"
        sx={{ maxHeight: "300px", overflowY: "auto", padding: "10px", margin: "-10px" }}
      >
        {currentCharacteristics.map((char) => (
          <Box key={char.id} sx={{ flex: "0 1 calc(50% - 10px)", minWidth: 0 }}>
            <CharacteristicField
              characteristic={char}
              value={getCharValue(char.id)}
              onChange={(value) => onCharacteristicChange(char.id, value)}
              currency={char.data_type === "currency" ? getCharCurrency(char.id) : undefined}
              onCurrencyChange={char.data_type === "currency" ? (c) => onCurrencyChange(char.id, c) : undefined}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
